import { getExperienceItemView } from 'checkout/lib/utils/experiences/view'
import { excludeNullOrUndefined } from 'checkout/utils'
import { isExperienceItem, isTransferItem } from 'lib/checkout'
import { DAY_MONTH_NAME_SHORT, SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { sum } from 'lib/arrayUtils'
import createSelector from 'lib/createSelector'
import moment from 'moment'
import { pluralizeToString } from 'lib/pluralize'
import { arrayResultMemoizeOptions } from 'checkout/lib/utils/misc/arrayResultMemoizeOptions'

export const getExperienceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.ExperienceItem> => items.filter(isExperienceItem),
)

export const getExperienceItemsView = createSelector(
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.experience.experienceTimes,
  (state: App.State) => state.checkout.cart.currencyCode,
  (
    experienceItems,
    experiences,
    experienceTimes,
    currencyCode,
  ): App.WithDataStatus<Array<App.Checkout.ExperienceItemView>> => {
    const itemViews = experienceItems.map(item => {
      return getExperienceItemView(item, experiences, experienceTimes, currencyCode)
    })

    return {
      hasRequiredData: itemViews.every(Boolean),
      data: itemViews.filter(excludeNullOrUndefined),
    }
  },
)

export const getTransferItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.TransferItem> => items.filter(isTransferItem),
  arrayResultMemoizeOptions,
)

export const getTransferItemsView = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.experience.experiences,
  (
    transferItems,
    experiences,
  ): App.WithDataStatus<Array<App.Checkout.TransferItem>> => {
    return {
      // make sure full experience data has been fetched for every transfer
      hasRequiredData: transferItems.every(transfer => !!experiences[transfer.experienceId]),
      data: transferItems,
    }
  },
)

export const getExperienceBreakdownView = createSelector(
  getExperienceItemsView,
  (viewWithStatus): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }

    const experienceBreakdownView = {
      title: 'Experiences',
      price: sum(viewWithStatus.data.map(i => i?.price ?? 0)),
      additionalInfoText: [],
      items: viewWithStatus.data.filter(excludeNullOrUndefined).map<App.Checkout.ItemBreakdownView>(expView => {
        const additionalInfoText = expView.ticketViews
          .filter(ticketView => !ticketView.unavailable)
          .map(ticketView => `${ticketView.count}x ${ticketView.name}`)

        if (expView.pickupPoint) { additionalInfoText.push(`Pick up: ${expView.pickupPoint.name}`) }

        const dateTimeLanguage = [
          expView.bookingDate && expView.bookingDate !== 'any' && moment.utc(expView.bookingDate).format(DAY_MONTH_NAME_SHORT),
          expView.bookingTime && moment.utc(`${expView.bookingDate}T${expView.bookingTime}`).format(SHORT_TIME_FORMAT_AM_PM),
          expView.language?.name,
        ].filter(excludeNullOrUndefined)

        if (dateTimeLanguage.length > 0) { additionalInfoText.push(dateTimeLanguage.join(' • ')) }

        return {
          title: expView.title,
          price: expView.price,
          additionalInfoText,
          itemType: 'experience',
          offerId: expView.experienceId,
          additionalElements: [],
          taxesAndFees: expView.taxesAndFees,
        }
      }),
    }

    return {
      hasRequiredData: true,
      data: [experienceBreakdownView],
    }
  },
)

export const getTransfersBreakdownView = createSelector(
  getTransferItemsView,
  (viewWithStatus): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }

    const transferBreakdownView:App.Checkout.PriceBreakdownView = {
      title: 'Transfers',
      price: sum(viewWithStatus.data.map(item => item.transfer.option!.price ?? 0)),
      additionalInfoText: [],
      items: viewWithStatus.data.filter(excludeNullOrUndefined).map<App.Checkout.ItemBreakdownView>(item => {
        return {
          title: item.transfer.title ?? '',
          price: item.transfer.option!.price,
          itemType: item.itemType,
          offerId: item.transfer.option!.id,
          additionalInfoText: [
            item.transfer.option!.name,
            pluralizeToString('passenger', item.transfer.option!.maxPassengers),
            `Maximum ${pluralizeToString('bag', item.transfer.option!.maxCheckedBags)}`,
          ],
          additionalElements: [],
        }
      }),
    }

    return {
      hasRequiredData: true,
      data: [transferBreakdownView],
    }
  },
)
